import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

interface ValuationGuidanceCardProps {
  companyId: string;
  onPreMoneyChange: (value: number) => void;
  preMoney: number;
}

export function ValuationGuidanceCard({
  companyId,
  onPreMoneyChange,
  preMoney,
}: ValuationGuidanceCardProps) {
  const [suggested, setSuggested] = useState<{ low: number; mid: number; high: number } | null>(
    null
  );
  const [mode, setMode] = useState<'suggested' | 'custom'>('suggested');
  const [customVal, setCustomVal] = useState('');

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('valuations')
        .select('fmv_per_share, as_of_date')
        .eq('company_id', companyId)
        .order('as_of_date', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const fmv = data[0].fmv_per_share;
        // Derive range from FMV
        const { data: capData } = await supabase.rpc('sm_load_cap_table', {
          company_id: companyId,
        });
        const totalShares =
          capData?.reduce((s: number, r: any) => s + Number(r.shares), 0) || 1000000;
        const mid = fmv * totalShares;
        setSuggested({ low: mid * 0.7, mid, high: mid * 1.4 });
        if (mode === 'suggested') onPreMoneyChange(mid);
      } else {
        // Fallback: check onboarding valuation from audit_log
        const { data: logs } = await supabase
          .from('audit_log')
          .select('details')
          .eq('company_id', companyId)
          .eq('action', 'ONBOARDING_VALUATION')
          .order('created_at', { ascending: false })
          .limit(1);

        if (logs && logs.length > 0) {
          const d = logs[0].details as any;
          if (d?.low && d?.high) {
            const mid = (d.low + d.high) / 2;
            setSuggested({ low: d.low, mid, high: d.high });
            if (mode === 'suggested') onPreMoneyChange(mid);
          }
        }
      }
    }
    load();
  }, [companyId]);

  useEffect(() => {
    if (mode === 'suggested' && suggested) {
      onPreMoneyChange(suggested.mid);
    } else if (mode === 'custom' && customVal) {
      onPreMoneyChange(parseFloat(customVal.replace(/[^0-9.]/g, '')) || 0);
    }
  }, [mode, customVal, suggested]);

  const fmt = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${(n / 1_000).toFixed(0)}K`;

  const benchmark = () => {
    if (!suggested || !preMoney) return null;
    if (preMoney < suggested.low)
      return { label: 'Below benchmark', color: 'text-amber-600', icon: AlertTriangle };
    if (preMoney > suggested.high)
      return { label: 'Above benchmark', color: 'text-amber-600', icon: AlertTriangle };
    return { label: 'Within benchmark', color: 'text-emerald-600', icon: CheckCircle };
  };

  const bm = benchmark();

  return (
    <div className="space-y-4">
      {suggested ? (
        <Card className="bg-primary/5 border-primary/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-primary" />
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Your valuation guidance
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Low</p>
                <p className="text-lg font-semibold tabular-nums">{fmt(suggested.low)}</p>
              </div>
              <div className="border-x border-primary/10">
                <p className="text-xs text-muted-foreground">Mid</p>
                <p className="text-2xl font-bold tabular-nums text-primary">{fmt(suggested.mid)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">High</p>
                <p className="text-lg font-semibold tabular-nums">{fmt(suggested.high)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            No valuation data available yet. Enter your pre-money valuation below.
          </CardContent>
        </Card>
      )}

      <RadioGroup
        value={mode}
        onValueChange={(v) => setMode(v as 'suggested' | 'custom')}
        className="space-y-3"
      >
        {suggested && (
          <div className="flex items-center gap-3">
            <RadioGroupItem value="suggested" id="suggested" />
            <Label htmlFor="suggested" className="cursor-pointer">
              Use suggested valuation ({fmt(suggested.mid)})
            </Label>
          </div>
        )}
        <div className="flex items-center gap-3">
          <RadioGroupItem value="custom" id="custom" />
          <Label htmlFor="custom" className="cursor-pointer">
            Enter my own pre-money valuation
          </Label>
        </div>
      </RadioGroup>

      {mode === 'custom' && (
        <div className="space-y-2">
          <Input
            type="text"
            placeholder="e.g. 5000000"
            value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
          />
          {bm && (
            <div className={`flex items-center gap-1.5 text-xs ${bm.color}`}>
              <bm.icon className="h-3.5 w-3.5" />
              {bm.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
